import { onMounted, ref, onUnmounted, computed, watchEffect, watch } from '@nuxtjs/composition-api'

type StickyParameters = {
  stickyStart?: number // The scroll position at which the component should become sticky
  stickyStop?: number // The scroll position at which the component should stop being sticky and freezes in place
  topShift?: number // The distance from the top of the screen when component is sticky
}

export function useStickyComponent(params: StickyParameters | (() => StickyParameters)) {
  const reactiveParams = computed(typeof params === 'function' ? params : () => params)

  const position = ref<'top' | 'sticky' | 'bottom'>('top') // Where is the component currently located
  const wrapperRef = ref<any>()
  const wrapperWidth = ref<number>()
  const wrapperHeight = ref<number>()

  const isSticky = computed(() => position.value !== 'top')

  let resizeObserver: ResizeObserver

  function setStickyFlag() {
    const underTopThreshold = window.scrollY > (reactiveParams.value.stickyStart ?? 0)
    const underBottomThreshold =
      reactiveParams.value.stickyStop !== undefined &&
      window.scrollY > reactiveParams.value.stickyStop - (reactiveParams.value.topShift ?? 0)

    if (!underTopThreshold) {
      position.value = 'top'
    } else if (!underBottomThreshold) {
      position.value = 'sticky'
    } else {
      position.value = 'bottom'
    }
  }

  function setSize() {
    wrapperWidth.value = wrapperRef.value?.offsetWidth
    wrapperHeight.value = wrapperRef.value?.offsetHeight
  }

  onMounted(() => {
    window.addEventListener('scroll', setStickyFlag)

    resizeObserver = new ResizeObserver((_entries) => setSize())
    resizeObserver.observe(document.body)
    setSize()
  })

  onUnmounted(() => {
    window.removeEventListener('scroll', setStickyFlag)
    resizeObserver?.disconnect()
  })

  watchEffect(() => {
    if (!wrapperRef.value) {
      return
    }

    // Set wrapper height to the same size as its content to avoid layout shifting when component becomes sticky
    if (isSticky.value) {
      wrapperRef.value.style.height = `${wrapperHeight.value}px`
    } else {
      wrapperRef.value.style.height = ''
    }
  })

  watch(reactiveParams, () => {
    setStickyFlag()
  })

  return {
    stickyStyle: computed<Record<string, unknown>>(() => ({
      width: wrapperWidth.value + 'px',
      top:
        position.value === 'top'
          ? undefined
          : position.value === 'sticky'
          ? (reactiveParams.value.topShift ?? 0) + 'px'
          : (reactiveParams.value.stickyStop ?? 0) + 'px',
      position: position.value === 'top' ? undefined : position.value === 'sticky' ? 'fixed' : 'absolute',
    })),
    wrapperRef,
    isSticky,
  }
}
